'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './general.module.css';

const steps = [
  {
    num: '01',
    title: 'Share Your Business Details',
    desc: 'Upload your services, hours, and location — or drop in your website URL and we extract everything automatically.',
    illus: (
      <div className={styles.illusStack}>
        <div className={`${styles.illusDoc} ${styles.illusDocBack2}`}>
          <span>yourbusiness.com</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocBack1}`}>
          <span>Business Hours</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocFront}`}>
          <span className={styles.illusDocIcon}>📋</span>
          <span>Services.pdf</span>
        </div>
        <div className={styles.illusAgent}>
          <span>🤖</span>
          <span>General Agent</span>
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Train Your Agent',
    desc: 'Name your agent, customize its greeting and tone, define how calls are handled, and set language preferences.',
    illus: (
      <div className={styles.illusPanel}>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Agent Name</span>
          <span className={styles.illusPanelValue}>Alex</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Tone</span>
          <span className={styles.illusPanelValue}>Friendly &amp; Professional</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Industry</span>
          <span className={styles.illusPanelValue}>Any Business</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Routing</span>
          <span className={`${styles.illusPanelValue} ${styles.illusPanelOn}`}>Enabled ✓</span>
        </div>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Claim Your Phone Number',
    desc: 'Choose a dedicated local or toll-free number for your business. Forward your existing number or start fresh immediately.',
    illus: (
      <div className={styles.illusPhone}>
        <div className={styles.illusPhoneLabel}>Your Local Number</div>
        <div className={styles.illusPhoneNum}>(555) 600-1100</div>
        <div className={styles.illusPhoneBadge}>✓ Ready to receive calls</div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Go Live 24/7',
    desc: 'Your general agent is on. Every customer call answered, every inquiry handled — no hold times, no missed calls.',
    illus: (
      <div className={styles.illusLive}>
        <div className={styles.illusLiveBadge}>
          <span className={styles.illusLiveDot} />
          LIVE
        </div>
        <ul className={styles.illusLiveList}>
          <li>No hold times</li>
          <li>No missed customers</li>
          <li>No extra staff needed</li>
        </ul>
      </div>
    ),
  },
];

export default function GeneralAgentPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, steps.length - 1));
    setCurrentSlide(clamped);
    const vp = viewportRef.current;
    if (!vp) return;
    const cards = vp.querySelectorAll('[data-card]');
    cards[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  return (
    <div className={styles.page}>

      {/* ── Navigation ── */}
      <NavBar />

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <Image
          src="/images/general.png"
          alt="General Agent background"
          fill
          priority
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroLayout}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>General AI Voice Agent</span>
            <h1 className={styles.heroTitle}>Any Business. Any Call. Handled.</h1>
            <p className={styles.heroSubtitle}>
              From retail stores and bank front desks to schools, telecom support, and beyond — your AI agent answers every call, handles every inquiry, and keeps every customer satisfied, 24/7.
            </p>
            <div className={styles.heroBtns}>
              <Link href="/register?industry=general" className={styles.heroBtnPrimary}>Get Started</Link>
              <Link href="#how-it-works" className={styles.heroBtnSecondary}>See How It Works</Link>
            </div>
          </div>

          {/* ── Book Demo Widget ── */}
          <div className={styles.audioWidget}>
            <div className={styles.audioWidgetHeader}>
              <div className={styles.audioAvatar}>AI</div>
              <div>
                <div className={styles.audioAgentName} style={{ marginLeft: '44px' }}>Sage</div>
                <div className={styles.audioAgentStatus} style={{ justifyContent: 'center' }}>
                  <span className={styles.audioStatusDotActive} />
                  <span style={{ fontWeight: 700 }}>General AI Agent</span>
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.65)', marginTop: '2px', textAlign: 'center' }}>Any Industry · Any Call</div>
              </div>
            </div>
            <div className={styles.audioWaveform}>
              {[...Array(16)].map((_, i) => (
                <span key={i} className={`${styles.audioWaveBar} ${styles.audioWaveBarActive}`} style={{ animationDelay: `${i * 0.07}s` }} />
              ))}
            </div>
            <Link href="/contact" className={styles.audioPlayBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              Book a Live Demo
            </Link>
            <p className={styles.audioLabel}>Free 15-minute demo · No commitment</p>
          </div>

        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresInner}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>ANY INDUSTRY · ALWAYS ON · ZERO WAIT TIMES</span>
            <h2>Every Customer Served.<br />Every Inquiry Resolved.<br />Every Time.</h2>
            <p>NovaMind AI handles your phone lines so your team doesn&apos;t have to — answering questions, routing calls, scheduling appointments, and updating customers 24/7 with zero hold times.</p>
          </div>
          <div className={styles.featuresGrid}>

            {/* Card 1 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusCallWrap}>
                  <div className={styles.illusCallBadge}>
                    <span className={styles.illusCallDot} /> Live · Incoming Call
                  </div>
                  <div className={styles.illusCallNumber}>(555) 600-1100</div>
                  <div className={styles.illusWaveBars}>
                    {[...Array(12)].map((_, i) => (
                      <span key={i} className={styles.illusWaveBar} style={{ animationDelay: `${i * 0.08}s` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Instant Call Answering & Routing</h3>
                <p className={styles.fcSubtitle}>AI answers 100% of inbound calls instantly and routes to the right department with zero hold time.</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusFaqCard}>
                  <div className={styles.illusFaqTitle}>Customer Inquiry</div>
                  <div className={styles.illusFaqRow}><span>Hours</span><span>Mon–Sat 9am–8pm</span></div>
                  <div className={styles.illusFaqRow}><span>Location</span><span>123 Main St</span></div>
                  <div className={styles.illusFaqRow}><span>Services</span><span>In-store &amp; Online</span></div>
                  <div className={styles.illusFaqBadge}>✓ Inquiry Resolved</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Customer Inquiry Handling</h3>
                <p className={styles.fcSubtitle}>Answers FAQs about hours, locations, services, and policies instantly — no staff needed.</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusApptCard}>
                  <div className={styles.illusApptTitle}>Appointment Booked</div>
                  <div className={styles.illusApptRow}><span>Customer</span><span>Sarah Thompson</span></div>
                  <div className={styles.illusApptRow}><span>Date</span><span>Wed, Apr 9</span></div>
                  <div className={styles.illusApptRow}><span>Time</span><span>11:00 AM</span></div>
                  <div className={styles.illusApptRow}><span>Service</span><span>Consultation</span></div>
                  <div className={styles.illusApptConfirm}>✓ Confirmed</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Appointment & Reservation Scheduling</h3>
                <p className={styles.fcSubtitle}>Books, confirms, reschedules, and cancels appointments for any type of business — 24/7.</p>
              </div>
            </div>

            {/* Card 4 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusStatusWrap}>
                  <div className={styles.illusStatusBadge}>📞 Incoming Call · After Hours</div>
                  <div className={styles.illusStatusMsg}>We&apos;re closed right now, but <strong>we&apos;ve got you covered!</strong></div>
                  <div className={styles.illusStatusEst}>Message recorded · <span>Team notified</span></div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>After-Hours & Overflow Call Handling</h3>
                <p className={styles.fcSubtitle}>Captures every call outside business hours or during peak times — no customer is ever left without a response.</p>
              </div>
            </div>

            {/* Card 5 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusRouteCard}>
                  <div className={styles.illusRouteTitle}>Smart Call Routing</div>
                  <div className={styles.illusRouteRow}><span>🏪</span><span>Retail Store</span></div>
                  <div className={styles.illusRouteRow}><span>🏦</span><span>Bank Front Desk</span></div>
                  <div className={styles.illusRouteRow}><span>🏫</span><span>School Info Line</span></div>
                  <div className={styles.illusRouteRow}><span>📞</span><span>Customer Support</span></div>
                  <div className={styles.illusRouteBadge}>✓ Routed Instantly</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Multi-department Call Routing</h3>
                <p className={styles.fcSubtitle}>Intelligently routes callers to the right team — works for retail, banking, schools, telecom, and more.</p>
              </div>
            </div>

            {/* Card 6 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusLangWrap}>
                  {[
                    { lang: 'EN', text: 'What are your store hours?' },
                    { lang: 'ES', text: '¿Cuáles son sus horarios?' },
                    { lang: 'ZH', text: '你们的营业时间是什么？' },
                  ].map((b, i) => (
                    <div key={i} className={styles.illusLangBubble}>
                      <span className={styles.illusLangTag}>{b.lang}</span>
                      <span className={styles.illusLangText}>{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Multi-language on Request</h3>
                <p className={styles.fcSubtitle}>Configured for the languages your customers speak — serve every caller in their preferred language.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.howItWorksHeader}>
          <div>
            <h2 className={styles.howTitle}>
              <em className={styles.howTitleItalic}>How</em> It Works.
            </h2>
            <p className={styles.howSubtitle}>From sign-up to live customer calls in under 5 minutes</p>
          </div>
          <div className={styles.sliderArrows}>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide - 1)}
              disabled={currentSlide === 0}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide + 1)}
              disabled={currentSlide === steps.length - 1}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.sliderViewport} ref={viewportRef}>
          {steps.map((step, i) => (
            <div
              key={i}
              data-card
              className={`${styles.slideCard} ${styles[`slideCard${i + 1}`]}`}
            >
              <span className={styles.slideNum}>{step.num}</span>
              <div className={styles.slideIllus}>{step.illus}</div>
              <div className={styles.slideText}>
                <h3 className={styles.slideTitle}>{step.title}</h3>
                <p className={styles.slideDesc}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <Image
          src="/images/retail.png"
          alt="CTA background"
          fill
          priority
          className={styles.ctaImage}
        />
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Automate Your Business Calls?</h2>
          <p className={styles.ctaSubtitle}>From retail and telecom to schools and government — any industry, any call volume, always answered professionally, 24/7.</p>
          <Link href="/register?industry=general" className={styles.ctaBtn}>Get Started</Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer />

    </div>
  );
}
